import { ClientConfig } from '@sanity/client';

import { ARTISTS_SMARTLING_LOCALES, CONSUMER_SMARTLING_LOCALES } from 'utils/i18n/localeConfig';

import { Vertical } from './vertical';

export type NavigationLinkData = {
  label: string;
  href: string;
};

export type NavigationSubMenuData = {
  label: string;
  menuKey: string;
  links: Array<NavigationLinkData>;
};

type LinksConfig = Array<NavigationLinkData | NavigationSubMenuData>;

export type NavigationConfig = {
  linksConfig: LinksConfig;
  colorSet?: `encore-${string}-set`;
};

export type FeatureConfig = {
  showHeaderGradient: boolean;
  configureNavigation: NavigationConfig | null;
  communityLink: string | null;
};

export type VerticalConfig = {
  cms: ClientConfig;
  domain: string;
  features: FeatureConfig;
  locales: Record<string, string>;
  theme: `encore-${string}-theme`;
};

export type VerticalConfigs = Record<Vertical, VerticalConfig>;

export const artistsLinksConfig: LinksConfig = [
  { label: 'Get started', href: 'https://artists.spotify.com/get-started' },
  { label: 'Features', href: 'https://artists.spotify.com/features' },
  {
    label: 'Resources',
    menuKey: 'resources',
    links: [
      { label: 'Videos', href: 'https://artists.spotify.com/videos' },
      { label: 'News', href: 'https://artists.spotify.com/blog' },
      { label: 'Events', href: 'https://artists.spotify.com/events' },
      {
        label: 'In Focus',
        href: 'https://artists.spotify.com/infocus?utm_campaign=top-nav&utm_medium=owned-product&utm_source=s4a',
      },
      {
        label: 'Fan Study',
        href: 'https://artists.spotify.com/fanstudy?utm_campaign=top-nav&utm_medium=owned-product&utm_source=s4a',
      },
      {
        label: 'Made to Be Found',
        href: 'https://artists.spotify.com/found?utm_campaign=top-nav&utm_medium=owned-product&utm_source=s4a',
      },
      {
        label: 'Loud & Clear',
        href: 'https://artists.spotify.com/loudandclear?utm_campaign=top-nav&utm_medium=owned-product&utm_source=s4a',
      },
      {
        label: 'Noteable',
        href: 'https://artists.spotify.com/noteable?utm_campaign=top-nav&utm_medium=owned-product&utm_source=s4a',
      },
    ],
  },
  { label: 'Help', href: 'https://artists-support.spotify.com' },
];

// These configs might be available in frontend, so don't add anything sensitive like secrets etc
const verticalConfigs: Record<Vertical, VerticalConfig> = {
  [Vertical.ARTIST]: {
    cms: {
      dataset: process.env.VERTICAL_ARTIST_DATASET ?? 'artists_production',
      projectId: 'tsbk0zvv',
      apiVersion: '2021-10-21',
    },
    domain: process.env.VERTICAL_ARTIST_DOMAIN ?? 'artists-support.spotify.com',
    features: {
      communityLink: null,
      showHeaderGradient: false,
      configureNavigation: {
        colorSet: 'encore-inverted-dark-set',
        linksConfig: artistsLinksConfig,
      },
    },
    locales: ARTISTS_SMARTLING_LOCALES,
    theme: 'encore-creator-light-theme',
  },
  [Vertical.CONSUMER]: {
    cms: {
      dataset: process.env.VERTICAL_CONSUMER_DATASET ?? 'production',
      projectId: 'tsbk0zvv',
      apiVersion: '2021-10-21',
    },
    domain: process.env.VERTICAL_CONSUMER_DOMAIN ?? 'support.spotify.com',
    features: {
      communityLink: 'https://community.spotify.com/t5/Spotify-Answers/tkb-p/Spotify-Answers',
      showHeaderGradient: true,
      configureNavigation: null,
    },
    locales: CONSUMER_SMARTLING_LOCALES,
    theme: 'encore-dark-theme',
  },
} as const;

const domainConfigs: Record<string, Vertical> = {
  [verticalConfigs[Vertical.ARTIST].domain]: Vertical.ARTIST,
  [verticalConfigs[Vertical.CONSUMER].domain]: Vertical.CONSUMER,
} as const;

export const getVerticalConfigs = (): Record<Vertical, VerticalConfig> => verticalConfigs;

export const getVerticalConfig = (vertical: Vertical): VerticalConfig => verticalConfigs[vertical];

export const getVertical = (domain: string): Vertical | undefined => domainConfigs[domain];
